import React from 'react';
import { ESTATUS_OPCIONES } from './constants/centro.constants';
import { CentroFormAlert } from './components/CentroFormAlert';
import { CentroFormTabs } from './components/CentroFormTabs';
import { CentroStatusPanel } from './components/CentroStatusPanel';
import { TabExpediente } from './components/tabs/TabExpediente';
import { TabGeneral } from './components/tabs/TabGeneral';
import { TabServicios } from './components/tabs/TabServicios';
import { TabUbicacion } from './components/tabs/TabUbicacion';
import { useCentroForm } from './hooks/useCentroForm';
import { useCentroFormTour } from './hooks/useCentroFormTour';
import { TourGuideButtons } from '../../../shared/tours/TourGuideButtons';

const CentroForm: React.FC = () => {
    const {
        urlParams,
        isEditMode,
        activeTab,
        setActiveTab,
        isLoading,
        isSaving,
        alert,
        setAlert,
        formData,
        slots,
        proveedores,
        proveedorNombre,
        estados,
        ciudades,
        tiposCentro,
        categorias,
        marcas,
        bancos,
        constanciaFiles,
        setConstanciaFiles,
        caratulaFiles,
        setCaratulaFiles,
        nuevoEstatus,
        setNuevoEstatus,
        comentarioEstatus,
        setComentarioEstatus,
        isChangingEstatus,
        mapRef,
        searchInputRef,
        catalogos,
        handleInputChange,
        handleProveedorChange,
        handleExpedienteChange,
        handleHorarioChange,
        handleAddSlot,
        handleSlotChange,
        handleRemoveSlot,
        handleStatusChange,
        handleSubmit,
    } = useCentroForm();

    const { Tour, startGeneralTour, startTabTour } = useCentroFormTour(isLoading, activeTab);

    if (isLoading) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status" />
            </div>
        );
    }

    const estatusBadge = ESTATUS_OPCIONES.find((o) => o.id === formData.clave_estatus);

    return (
        <div className="container-fluid px-0">
            {Tour}
            <CentroFormAlert alert={alert} onDismiss={() => setAlert(null)} />

            <div className="row g-4">
                <div className={isEditMode ? 'col-xl-9' : 'col-xl-12'}>
                    <form onSubmit={handleSubmit} className="animate__animated animate__fadeIn">
                        <div className="card shadow-sm border-0 mb-4">
                            <div
                                id="centro-form-header"
                                className="card-header border-bottom py-3 d-flex flex-wrap justify-content-between align-items-center gap-2"
                            >
                                <h5 className="mb-0 fw-bold">
                                    <i className={`fas fa-${isEditMode ? 'edit' : 'plus-circle'} text-primary me-2`} />
                                    {isEditMode ? 'Editar Centro de Servicio' : 'Nuevo Centro de Servicio'}
                                </h5>
                                <div className="d-flex flex-wrap align-items-center gap-2">
                                    <TourGuideButtons
                                        id="centro-form-tour-actions"
                                        onGeneralTour={startGeneralTour}
                                        onDetailTour={startTabTour}
                                        detailLabel="¿Cómo llenar esta pestaña?"
                                    />
                                    {isEditMode && estatusBadge && (
                                        <span className={`badge bg-${estatusBadge.color}`}>{estatusBadge.label}</span>
                                    )}
                                </div>
                            </div>
                            <div className="card-body p-4">
                                <div id="centro-form-tabs">
                                    <CentroFormTabs activeTab={activeTab} onTabChange={setActiveTab} />
                                </div>

                                <div className="tab-content border border-top-0 rounded-bottom p-4 shadow-sm">
                                    <div className={`tab-pane fade ${activeTab === 'atencion' ? 'show active' : ''}`}>
                                        <TabGeneral
                                            isEditMode={isEditMode}
                                            formData={formData}
                                            proveedores={proveedores}
                                            proveedorNombre={proveedorNombre}
                                            tiposCentro={tiposCentro}
                                            bancos={bancos}
                                            slots={slots}
                                            caratulaFiles={caratulaFiles}
                                            setCaratulaFiles={setCaratulaFiles}
                                            onInputChange={handleInputChange}
                                            onProveedorChange={handleProveedorChange}
                                            onHorarioChange={handleHorarioChange}
                                            onAddSlot={handleAddSlot}
                                            onSlotChange={handleSlotChange}
                                            onRemoveSlot={handleRemoveSlot}
                                        />
                                    </div>
                                    <div className={`tab-pane fade ${activeTab === 'servicios' ? 'show active' : ''}`}>
                                        <TabServicios
                                            formData={formData}
                                            categorias={categorias}
                                            subcategorias={catalogos.subcategorias}
                                            detalles={catalogos.detalles}
                                            marcas={marcas}
                                            modelos={catalogos.modelos}
                                            selectedCat={catalogos.selectedCat}
                                            setSelectedCat={catalogos.setSelectedCat}
                                            selectedSub={catalogos.selectedSub}
                                            setSelectedSub={catalogos.setSelectedSub}
                                            selectedDet={catalogos.selectedDet}
                                            setSelectedDet={catalogos.setSelectedDet}
                                            selectedMarca={catalogos.selectedMarca}
                                            setSelectedMarca={catalogos.setSelectedMarca}
                                            selectedModelo={catalogos.selectedModelo}
                                            setSelectedModelo={catalogos.setSelectedModelo}
                                            onAddService={catalogos.handleAddService}
                                            onRemoveService={catalogos.handleRemoveService}
                                        />
                                    </div>
                                    <div className={`tab-pane fade ${activeTab === 'expediente' ? 'show active' : ''}`}>
                                        <TabExpediente formData={formData} onExpedienteChange={handleExpedienteChange} />
                                    </div>
                                    <div className={`tab-pane fade ${activeTab === 'location' ? 'show active' : ''}`}>
                                        <TabUbicacion
                                            formData={formData}
                                            estados={estados}
                                            ciudades={ciudades}
                                            mapRef={mapRef}
                                            searchInputRef={searchInputRef}
                                            onInputChange={handleInputChange}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="card-footer text-end py-3">
                                <a
                                    href={`proveedores?accion=edit&id=${urlParams.get('idProveedor')}`}
                                    className="btn border me-2 fw-bold"
                                >
                                    Cancelar
                                </a>
                                <button
                                    id="centro-form-save"
                                    type="submit"
                                    className="btn btn-success fw-bold px-5"
                                    disabled={isSaving}
                                >
                                    {isSaving ? (
                                        <i className="fas fa-spinner fa-spin" />
                                    ) : (
                                        <i className="fas fa-save" />
                                    )}{' '}
                                    {isEditMode ? 'Guardar Cambios' : 'Crear Centro'}
                                </button>
                            </div>
                        </div>
                    </form>
                </div>

                {isEditMode && (
                    <div className="col-xl-3">
                        <CentroStatusPanel
                            nuevoEstatus={nuevoEstatus}
                            estatusActual={formData.clave_estatus}
                            comentarioEstatus={comentarioEstatus}
                            isChangingEstatus={isChangingEstatus}
                            onEstatusChange={setNuevoEstatus}
                            onComentarioChange={setComentarioEstatus}
                            onSubmit={handleStatusChange}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default CentroForm;
